/**
 * Web Push subscription helpers.
 *
 * Supported() tells the UI whether this browser can receive push at all.
 * permissionState() returns "granted" | "denied" | "default" | "unsupported".
 * subscribe() asks the browser for permission, creates a PushSubscription,
 *   and POSTs it to the backend.
 * unsubscribe() tears down the PushSubscription and tells the backend.
 * currentEndpoint() returns the current endpoint if any.
 */

import { api } from "@/lib/api";

export function pushSupported() {
  return (
    typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window
  );
}

export function permissionState() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission; // "granted" | "denied" | "default"
}

// urlBase64 → Uint8Array (required by PushManager.subscribe)
function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration() {
  const reg = await navigator.serviceWorker.ready;
  return reg;
}

export async function currentEndpoint() {
  if (!pushSupported()) return null;
  try {
    const reg = await getRegistration();
    const sub = await reg.pushManager.getSubscription();
    return sub ? sub.endpoint : null;
  } catch { return null; }
}

export async function subscribe({ city } = {}) {
  if (!pushSupported()) throw new Error("This browser doesn't support web push.");
  // Request permission if needed.
  if (Notification.permission === "default") {
    const res = await Notification.requestPermission();
    if (res !== "granted") throw new Error("Notifications permission denied.");
  } else if (Notification.permission !== "granted") {
    throw new Error("Notifications are blocked. Enable them in your browser settings.");
  }
  // Fetch server's VAPID public key.
  const { data: vk } = await api.get("/push/vapid-key");
  if (!vk?.public_key) throw new Error("Server VAPID key unavailable.");
  const reg = await getRegistration();
  // Re-use existing sub if any, else create.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(vk.public_key),
    });
  }
  const json = sub.toJSON();
  // Send keys to backend.
  await api.post("/push/subscribe", {
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    city: city || null,
    user_agent: navigator.userAgent,
  });
  return sub.endpoint;
}

export async function unsubscribe() {
  if (!pushSupported()) return;
  try {
    const reg = await getRegistration();
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      try { await api.post("/push/unsubscribe", { endpoint }); } catch { /* noop */ }
    }
  } catch { /* noop */ }
}
